import { BACKEND_API_URL } from "@/shared/config/app";
import { getAttributionPayload } from "@/shared/lib/analytics";
import { referralBody } from "@/shared/lib/referral";
import { sendAuthed } from "./recognize";

/**
 * Пакет «+60 распознаваний» (владелец 03.11.2026): ещё 60 блюд на 30 дней — 3 блюда в день вместо одного.
 * site_server POST /api/food/pack/checkout — цену выбирает сервер ($9.90), он же проверяет, что у человека есть
 * диагностика диеты, и отдаёт ссылку Stripe. После оплаты Stripe возвращает на returnPath с
 * ?food_paid=1&session_id=… (FoodPackPurchaseRoot). Сеанс — тот же Bearer, что у распознавания.
 */

export const FOOD_PACK_CHECKOUT_URL = `${BACKEND_API_URL}/api/food/pack/checkout`;

/** Метка возврата со Stripe после оплаты пакета (site_server food pack return). */
export const FOOD_PAID_PARAM = "food_paid";

export class FoodPackError extends Error {
    /** auth_required | no_diet_diagnostic | session_not_allowed | too_many_requests | no_url | network | http_5xx… */
    code: string;
    status: number;
    constructor(code: string, status: number) {
        super(code);
        this.name = "FoodPackError";
        this.code = code;
        this.status = status;
    }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const codeOf = (body: unknown, status: number): string => {
    if (!isRecord(body)) return `http_${status}`;
    const d = body.detail;
    return str(body.error) || (isRecord(d) ? str(d.error) : str(d)) || `http_${status}`;
};

/** POST /api/food/pack/checkout → адрес оплаты. Цены в запросе нет: её выбирает сервер. */
export const startFoodPackCheckout = async (opts: { returnPath?: string | null; lang: string }): Promise<{ url: string }> => {
    const attribution = getAttributionPayload();
    let res: Response;
    try {
        res = await sendAuthed(FOOD_PACK_CHECKOUT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                returnPath: opts.returnPath || undefined,
                lang: opts.lang,
                ...referralBody(),
                ...(attribution ? { attribution } : {}),
            }),
        });
    } catch {
        throw new FoodPackError("network", 0);
    }
    let body: unknown = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }
    if (!res.ok) throw new FoodPackError(codeOf(body, res.status), res.status);
    // {url} или, как у пакетов поиска, {data: {url}}
    const url = isRecord(body) ? str(body.url) || (isRecord(body.data) ? str(body.data.url) : "") : "";
    if (!/^https:\/\//i.test(url)) throw new FoodPackError("no_url", res.status);
    return { url };
};
